import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { HelpCircle } from "lucide-react";

interface FAQItem {
  question: string;
  answer: string;
}

export default function FAQSection() {
  const faqs: FAQItem[] = [
    {
      question: "How does the 10% rental income Dubai guarantee work?",
      answer: "Suite Global Estate guarantees a minimum 10% annual rental return for the first 3 years after handover. This means if your property is valued at $250,000, you'll receive at least $25,000 per year in rental income, paid quarterly. If market rates exceed 10%, you receive the higher amount. This guarantee is backed by the developer and included in your purchase contract."
    },
    {
      question: "How can I get a Dubai Golden Visa through property investment?",
      answer: "The UAE Golden Visa is a long-term residency program that allows you and your family to live, work, and study in Dubai. Property investors who purchase real estate worth AED 2 million (~$545,000) or more are eligible for a 10-year renewable Golden Visa. For investments between $250,000-$545,000, you may qualify for a 5-year investor visa. The visa includes your spouse, children, and up to one domestic helper."
    },
    {
      question: "Is Dubai property investment for foreigners safe and legal?",
      answer: "Yes, foreigners can own freehold property in designated areas of Dubai, including Motor City. You receive 100% ownership rights with full title deed registration at the Dubai Land Department. There are no restrictions on selling, renting, or transferring the property. You can also leave the property to your heirs without inheritance tax."
    },
    {
      question: "What is the Dubai property payment plan for this project?",
      answer: "Our flexible 3-year payment plan requires only 20% down payment at booking. The remaining 80% is divided into equal quarterly installments over 36 months. For example, on a $250,000 property: $50,000 down payment, then approximately $5,555 per quarter for 3 years. No interest or hidden fees. Early payment discounts are available."
    },
    {
      question: "Is this a tax-free property Dubai investment?",
      answer: "Dubai has 0% property tax, 0% income tax on rental income, and 0% capital gains tax when you sell. The only annual costs are: (1) Service charges for building maintenance (~$5-8 per sq ft annually), (2) DEWA utility connection fee (~$130/year), and (3) optional property management fees if you choose full rental management services."
    },
    {
      question: "How do I manage my expat property in Dubai?",
      answer: "Suite Global Estate offers comprehensive property management services. We handle tenant screening, lease agreements, rent collection, maintenance coordination, and annual renewals. Our management fee is 5% of annual rental income, and we guarantee occupancy. You receive rental income directly to your bank account quarterly, with full transparency reports."
    },
    {
      question: "When will my Dubai property for sale be ready?",
      answer: "The Motor City project is scheduled for completion in December 2027. Handover will begin in Q1 2028. You'll receive advance notice 90 days before handover to prepare for property inspection and final registration. The developer has a strong track record of on-time delivery. Your payment plan is structured to align with construction milestones."
    },
    {
      question: "Can I sell my Dubai apartment investment before it's completed?",
      answer: "You can sell your property at any time, even during the construction phase. Many investors sell pre-completion to capture capital appreciation (typically 15-25% in Dubai's growing market). Suite Global Estate offers free resale assistance, including marketing, buyer screening, and Dubai Land Department transfer procedures. Transfer fees are approximately 4% of sale price (2% buyer, 2% seller)."
    },
    {
      question: "What are the hidden costs of a Dubai residential investment?",
      answer: "Total additional costs are approximately 7-9% of property value: (1) Dubai Land Department registration fee: 4% of purchase price, (2) Real estate agent commission: 2% (one-time), (3) Mortgage registration fee: if applicable, (4) Property valuation: ~$300, (5) DEWA connection: ~$130. We provide a detailed cost breakdown before you commit."
    },
    {
      question: "Can I get a mortgage to invest in Dubai from India, Pakistan, Turkey, UK, Germany, or Canada?",
      answer: "Yes, several UAE banks offer mortgages to foreign investors with competitive rates (typically 4-6% annual interest). You can finance up to 75% of the property value for properties under $1M. Required documents include passport, visa, salary certificate, and bank statements. We work with mortgage brokers who specialize in expat financing and can assist with the application process."
    },
    {
      question: "What currency is used for a USD investment in Dubai?",
      answer: "All transactions are in UAE Dirhams (AED), which is pegged to the US Dollar at a fixed rate (1 USD = 3.67 AED). This eliminates exchange rate volatility for USD-based investors. We accept international wire transfers, and you can pay from any country. For non-USD investors, we recommend consulting your bank about forward contracts to lock in favorable exchange rates."
    },
    {
      question: "What amenities are included in this affordable Dubai property?",
      answer: "Motor City offers 40+ world-class amenities: infinity swimming pools, state-of-the-art fitness center, spa and wellness facilities, children's playground and daycare, landscaped gardens and jogging tracks, 24/7 security with CCTV, covered parking, concierge services, retail shops and restaurants, sports courts (tennis, basketball), and proximity to golf courses. All amenities are included in your service charge."
    }
  ];

  return (
    <section className="py-20 bg-background">
      <div className="container">
        <div className="max-w-4xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-full font-semibold text-sm mb-4">
              <HelpCircle className="w-4 h-4" />
              <span>FREQUENTLY ASKED QUESTIONS</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-foreground">
              Your Dubai Real Estate Investment Questions Answered
            </h2>
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
              Find answers to your questions about Dubai property investment for foreigners, high ROI Dubai apartments, and the Dubai Golden Visa. Your journey to a successful international property investment in Dubai starts here.
            </p>
          </div>

          {/* FAQ Accordion */}
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`}
                className="bg-card border border-border rounded-lg px-6 shadow-sm hover:shadow-md transition-shadow"
              >
                <AccordionTrigger className="text-left text-lg font-semibold text-card-foreground hover:text-primary py-6">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed pb-6 text-base">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          {/* CTA Box */}
          <div className="mt-16 bg-gradient-to-br from-primary/10 to-accent/10 rounded-2xl p-8 md:p-12 text-center border border-primary/20">
            <h3 className="text-2xl md:text-3xl font-bold mb-4 text-foreground">
              Still Have Questions?
            </h3>
            <p className="text-lg text-muted-foreground mb-6 max-w-2xl mx-auto">
              Our investment specialists are ready to provide personalized answers and guide you through every step of your Dubai property investment journey.
            </p>
            <a 
              href="#contact-form"
              className="inline-flex items-center justify-center px-10 py-5 bg-primary text-primary-foreground rounded-lg font-semibold text-lg hover:bg-primary/90 transition-all hover:scale-105 shadow-lg"
            >
              Fill Out Contact Form
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
